import classNames from "classnames";
import React, { useEffect } from "react";
import ReactDOMServer from "react-dom/server";
import BFButton from "../../general/Button/BFButton";
import BFDropdown from "../../general/Dropdown/BFDropdown";
import { DropdownButton } from "../../general/Dropdown/BFDropdownContent";
import { PAGECONFIG_TO_PIXEL_RATIO } from "./BFDocument.service";
import "./BFDocumentView.scss";

export const DOCUMENT_FONT_FAMILY = `Arial, sans-serif`;
export type PageConfig = {
  marginLeft: number;
  marginRight: number;
  marginTop: number;
  marginBottom: number;
  pageWidth: number;
  pageHeight: number;
};

export const PAGE_CONFIGS: { [key: string]: PageConfig } = {
  DIN4: {
    marginLeft: 20,
    marginRight: 20,
    marginTop: 20,
    marginBottom: 20,
    pageWidth: 210,
    pageHeight: 297,
  },
};

interface BFDocumentViewProps {
  contentHtml: React.ReactNode;
  pageConfig?: PageConfig;
  withoutMargin?: boolean;
  currentVariable?: string;
}

const BFDocumentView = (props: BFDocumentViewProps) => {
  const [scaleFactor, setScaleFactor] = React.useState(1);
  const documentRef = React.useRef<HTMLDivElement>(null);

  const setScale = (scale: number) => {
    setScaleFactor(scale);
    if (documentRef.current) {
      documentRef.current.style.transform = `scale(${scale})`;
    }
  };

  const scaleFactors: DropdownButton[] = [
    {
      type: "button",
      text: "50%",
      onSelect: () => {
        setScale(0.5);
      },
      active: scaleFactor === 0.5,
    },
    {
      type: "button",
      text: "100%",
      onSelect: () => {
        setScale(1);
      },
      active: scaleFactor === 1,
    },
    {
      type: "button",
      text: "150%",
      onSelect: () => {
        setScale(1.5);
      },
      active: scaleFactor === 1.5,
    },
    {
      type: "button",
      text: "200%",
      onSelect: () => {
        setScale(2);
      },
      active: scaleFactor === 2,
    },
  ];
  return (
    <div className={classNames("bf-document-view")}>
      <div className="action">
        <BFDropdown
          label={
            scaleFactors.find((e) => e.type === "button" && e.active)?.text ||
            "-"
          }
          placement="bottomEnd"
          items={scaleFactors}
          renderToggle={(rProps) => (
            <BFButton appearance="outline-white" size="xs" {...rProps}>
              {scaleFactors.find((e) => e.type === "button" && e.active)
                ?.text || "-"}
            </BFButton>
          )}
        />
      </div>
      <div
        className="scroll-container"
        onDoubleClick={() => {
          setScale(scaleFactor === 1 ? 2 : 1);
        }}
      >
        <BFDocumentViewContent {...props} documentRef={documentRef} />
      </div>
    </div>
  );
};

export default BFDocumentView;

export const BFDocumentViewContent = (
  props: BFDocumentViewProps & { documentRef?: React.RefObject<HTMLDivElement> }
) => {
  const config = props.pageConfig || PAGE_CONFIGS.DIN4;

  // Scroll to the element with matching data-variable-id if currentVariable is provided.
  useEffect(() => {
    if (
      props.currentVariable &&
      props.documentRef &&
      props.documentRef.current
    ) {
      const target = props.documentRef.current.querySelector(
        `[data-variable-id="${props.currentVariable}"]`
      );
      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  }, [props.currentVariable, props.documentRef]);

  return (
    <div
      ref={props.documentRef}
      className={classNames("document")}
      style={{
        paddingTop: props.withoutMargin
          ? 0
          : config.marginTop * PAGECONFIG_TO_PIXEL_RATIO,
        paddingBottom: props.withoutMargin
          ? 0
          : config.marginBottom * PAGECONFIG_TO_PIXEL_RATIO,
        paddingLeft: props.withoutMargin
          ? 0
          : config.marginLeft * PAGECONFIG_TO_PIXEL_RATIO,
        paddingRight: props.withoutMargin
          ? 0
          : config.marginRight * PAGECONFIG_TO_PIXEL_RATIO,
        width: props.withoutMargin
          ? undefined
          : config.pageWidth * PAGECONFIG_TO_PIXEL_RATIO,
        minHeight: props.withoutMargin
          ? undefined
          : config.pageHeight * PAGECONFIG_TO_PIXEL_RATIO,
      }}
    >
      <style>
        {` 
          img { max-width: 100%; }
          p { font-family:${DOCUMENT_FONT_FAMILY}; font-size: var(--font-size-sm); }
          h1 { font-family:${DOCUMENT_FONT_FAMILY}; font-size: var(--font-size-md); }
          h2 { font-family:${DOCUMENT_FONT_FAMILY}; font-size: 14px; }
          h3 { font-family:${DOCUMENT_FONT_FAMILY}; font-size: var(--font-size-sm); }
      `}
      </style>
      {props.contentHtml}
    </div>
  );
};

export const getDocumentViewContentAsHtml = (
  html: string,
  pageConfig?: PageConfig
) => {
  return ReactDOMServer.renderToStaticMarkup(
    <BFDocumentViewContent
      contentHtml={<div dangerouslySetInnerHTML={{ __html: html }}></div>}
      pageConfig={pageConfig}
      withoutMargin
    />
  );
};
